import {useContext, useState} from 'react';
import {ScrollView, StyleSheet, View} from 'react-native';
import {Button, DataTable, IconButton, Title} from 'react-native-paper';
import {useMutation, useQueryClient} from 'react-query';
import firestore from '@react-native-firebase/firestore';

import {UserContext} from '../App';
import {useAppSettings} from '../components/AppSettings';
import {
  CreditTransaction,
  filterLimitedPartners,
  LimitedPartner,
  Member,
  Reservation,
  useCreditTransactions,
  useFetchLimitedPartners,
  useMembers,
} from '../hooks/useDomainData';
import {
  usePeople,
  useAuthPersonInfo,
  Person,
  PersonInfo,
} from '../hooks/usePersonInfo';
import {CreditTransactionEditor} from '../components/FormikStoreEditors/CreditTransactionEditor';

function Transactions() {
  const user = useContext(UserContext);

  let limitedPartners = useFetchLimitedPartners();
  const members = useMembers();
  const people = usePeople();
  const authPersonInfo = useAuthPersonInfo();
  let transactions = useCreditTransactions();

  const [txnDialogVisible, setTxnDialogVisible] = useState(false);
  const [currentCreditTransaction, setCurrentCreditTransaction] = useState<
    CreditTransaction | undefined
  >();

  const handleCreateEditCreditTransaction = (
    creditTransaction?: CreditTransaction,
  ) => {
    // console.log('should edit credit transactionPath: ' + creditTransaction);
    setCurrentCreditTransaction(creditTransaction);
    setTxnDialogVisible(true);
  };

  if (!user) {
    return null;
  }

  let loading = true;

  if (!authPersonInfo?.isAdmin && !authPersonInfo?.isGuestRelations) {
    limitedPartners = filterLimitedPartners(
      limitedPartners,
      members,
      authPersonInfo?.person.personId ? [authPersonInfo.person.personId] : [],
    );
    // console.log('transactions limitedpartner size ' + limitedPartners.length);

    // filter down to just the ones for our current limited partner(s)
    transactions = transactions?.filter(txn =>
      limitedPartners?.find(
        lp => lp.limitedPartnerId === txn.limitedPartner?.limitedPartnerId,
      ),
    );
  }

  if (transactions && limitedPartners && members && people && authPersonInfo) {
    loading = false;
  }

  return (
    <>
      <View style={styles.horizontalCentered}>
        <Title style={styles.title}>
          Transactions, Relevant to Current Reservation Year
        </Title>
        {(authPersonInfo?.isAdmin || authPersonInfo?.isGuestRelations) && (
          <Button
            mode="outlined"
            onPress={() => handleCreateEditCreditTransaction()}>
            Create Transaction
          </Button>
        )}
      </View>
      <ScrollView style={[styles.content]}>
        <CreditTransactionList
          loading={loading}
          transactions={transactions}
          limitedPartners={limitedPartners}
          members={members}
          people={people}
          authPersonInfo={authPersonInfo}
        />
      </ScrollView>
      <CreditTransactionEditor
        allLimitedPartners={limitedPartners ?? []}
        filteredReservations={[] /* FIXME need to fetch reservations */}
        dialogVisible={txnDialogVisible}
        setDialogVisible={(visible: boolean) => {
          setTxnDialogVisible(visible);
          if (!visible) {
            setCurrentCreditTransaction(undefined);
          }
        }}
        creditTransaction={currentCreditTransaction}
      />
    </>
  );
}

export interface CreditTransactionListProps {
  loading: boolean;
  transactions?: readonly CreditTransaction[];
  reservations?: readonly Reservation[];
  limitedPartners?: LimitedPartner[];
  members?: readonly Member[];
  people?: readonly Person[];
  authPersonInfo?: PersonInfo;
}

export function CreditTransactionList(props: CreditTransactionListProps) {
  const queryClient = useQueryClient();
  const [txnDialogVisible, setTxnDialogVisible] = useState(false);
  const [currentCreditTransaction, setCurrentCreditTransaction] = useState<
    CreditTransaction | undefined
  >();

  const handleDeleteCreditTransaction = useMutation(
    (txn: CreditTransaction) => {
      console.log('should delete credit transaction: ' + txn.ref.path);
      return firestore()
        .collection('creditTransactions')
        .doc(txn.ref.id)
        .delete();
    },
    {
      onSettled: () => {
        console.log('deleting credit transaction succeeded');
        return queryClient.invalidateQueries('creditTransactions');
      },
    },
  );

  const handleCreateEditCreditTransaction = (
    creditTransaction?: CreditTransaction,
  ) => {
    // console.log('should edit credit transactionPath: ' + creditTransaction);
    setCurrentCreditTransaction(creditTransaction);
    setTxnDialogVisible(true);
  };

  const appSettings = useAppSettings();
  const themedStyles = StyleSheet.create({
    limitedPartnerContainer: {
      marginTop: 5,
      paddingTop: 10,
      padding: 10,
      borderTopWidth: 1,
      borderColor: appSettings.currentTheme.colors.primary,
    },
  });

  return (
    <>
      <DataTable style={themedStyles.limitedPartnerContainer}>
        <DataTable.Header>
          <DataTable.Title>Transaction ID</DataTable.Title>
          <DataTable.Title>Transaction Date</DataTable.Title>
          <DataTable.Title>Limited Partner</DataTable.Title>
          <DataTable.Title>Credits</DataTable.Title>
          <DataTable.Title style={styles.widerCell}>
            Credit Type
          </DataTable.Title>
          <DataTable.Title>Transaction Type</DataTable.Title>
          <DataTable.Title>Annual-managed</DataTable.Title>
          {(props.authPersonInfo?.isAdmin ||
            props.authPersonInfo?.isGuestRelations) && (
            <DataTable.Title>Actions</DataTable.Title>
          )}
        </DataTable.Header>

        {(!props.authPersonInfo || !props.transactions || props.loading) && (
          <DataTable.Row>
            <DataTable.Cell>loading...</DataTable.Cell>
          </DataTable.Row>
        )}
        {props.authPersonInfo &&
          !props.loading &&
          props.transactions?.length === 0 && (
            <DataTable.Row>
              <DataTable.Cell>No transactions found</DataTable.Cell>
            </DataTable.Row>
          )}
        {props.authPersonInfo &&
          props.transactions &&
          !props.loading &&
          props.transactions.map(txn => (
            <DataTable.Row
              key={txn.creditTransactionId}
              onPress={() => handleCreateEditCreditTransaction(txn)}>
              <DataTable.Cell>{txn.creditTransactionId}</DataTable.Cell>
              <DataTable.Cell>
                {txn.allocationDateJS?.toDateString()}
              </DataTable.Cell>
              <DataTable.Cell>
                {txn.limitedPartner?.limitedPartnerId}){' '}
                {txn.limitedPartner?.limitedPartnerName}
              </DataTable.Cell>
              <DataTable.Cell>{txn.credits}</DataTable.Cell>
              <DataTable.Cell style={styles.widerCell}>
                {txn.creditType?.fund?.fundCode} {txn.creditType?.creditType}
              </DataTable.Cell>
              <DataTable.Cell>{txn.transactionType}</DataTable.Cell>
              <DataTable.Cell>
                {txn.managedAutomatically !== undefined
                  ? txn.managedAutomatically
                    ? 'True'
                    : 'False'
                  : 'False'}
              </DataTable.Cell>
              {(props.authPersonInfo?.isAdmin ||
                props.authPersonInfo?.isGuestRelations) && (
                <DataTable.Cell>
                  <>
                    {(props.authPersonInfo.isAdmin ||
                      props.authPersonInfo.isGuestRelations) && (
                      <IconButton
                        icon="pencil"
                        size={18}
                        color={appSettings.currentTheme.colors.primary}
                        onPress={() => handleCreateEditCreditTransaction(txn)}
                      />
                    )}
                    {props.authPersonInfo.isAdmin && (
                      <IconButton
                        icon="delete"
                        size={18}
                        color={appSettings.currentTheme.colors.primary}
                        onPress={() =>
                          handleDeleteCreditTransaction.mutate(txn)
                        }
                      />
                    )}
                  </>
                </DataTable.Cell>
              )}
            </DataTable.Row>
          ))}
      </DataTable>
      <CreditTransactionEditor
        allLimitedPartners={props.limitedPartners ?? []}
        filteredReservations={props.reservations ?? []}
        dialogVisible={txnDialogVisible}
        setDialogVisible={(visible: boolean) => {
          setTxnDialogVisible(visible);
          if (!visible) {
            setCurrentCreditTransaction(undefined);
          }
        }}
        creditTransaction={currentCreditTransaction}
      />
    </>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    position: 'relative',
    backgroundColor: '#fff',
  },
  content: {
    paddingHorizontal: 20,
  },
  profile: {
    marginTop: -50,
    paddingVertical: 10,
  },
  avatar: {
    borderColor: '#fff',
    borderWidth: 5,
    elevation: 4,
  },
  providers: {
    backgroundColor: '#F6F7F8',
    elevation: 4,
    flexDirection: 'row',
    justifyContent: 'space-evenly',
    marginVertical: 30,
    padding: 20,
  },
  fab: {
    position: 'absolute',
    margin: 16,
    right: 0,
    bottom: 0,
  },
  center: {
    width: '100%',
    alignItems: 'center',
  },
  title: {
    padding: 10,
    paddingHorizontal: 20,
  },
  horizontalCentered: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignContent: 'center',
  },
  widerCell: {
    minWidth: 250,
  },
});

export default Transactions;
